import React from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { Button, Col, Row, Segmented, Spin, Typography } from 'components';
import { PinIcon, PlusIcon, TableEmptyIcon } from 'components/common/icons';
import { useCommunicationHub } from './useCommunicationHub';
import { NoteGroup } from './note/NoteGroup';
import { NoteAddUpdateModal } from './addEdit';
import styles from './styles.module.scss';

const { Title, Text } = Typography;

export type CommunicationHubEntityType =
  | 'SUPPLIER'
  | 'BRAND'
  | 'CLIENT'
  | 'LEAD'
  | 'DEAL'
  | 'PURCHASE_REQUEST'
  | 'PURCHASE_ORDER';

interface ICommunicationHub {
  entity: CommunicationHubEntityType;
  hasAccessToWrite: boolean;
  hasAccessToOtherNoteDelete: boolean;
  hasAccessToOtherNoteWrite: boolean;
  isFinalStatus?: boolean;
  isTabsVisible?: boolean;
}

export const CommunicationHub = React.memo((props: ICommunicationHub) => {
  const {
    hasAccessToOtherNoteWrite,
    hasAccessToOtherNoteDelete,
    hasAccessToWrite,
    entity,
    isTabsVisible,
    isFinalStatus,
  } = props;
  const {
    onAddModalStateChange,
    notes,
    pinnedNotes,
    addModalState,
    isFetchingNextPage,
    lastNoteRef,
    firstNoteRef,
    isLoading,
    tabOptions,
    onActiveTabChange,
    activeTab,
  } = useCommunicationHub({ entity });
  return (
    <Spin spinning={isLoading} indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}>
      <Row gutter={[0, 12]} justify={'end'} className={styles.wrapper}>
        <Col span={24} className={styles.top_bar}>
          <Row align={'middle'} justify={'space-between'}>
            <Col>
              {isTabsVisible && <Segmented options={tabOptions} value={activeTab} onChange={onActiveTabChange} />}
            </Col>
            {hasAccessToWrite && !isFinalStatus && (
              <Col className={styles.button}>
                <Button
                  block
                  variant={'outlined'}
                  size={'middle'}
                  color={'primary'}
                  onClick={() => onAddModalStateChange(true)}
                  icon={<PlusIcon size={'small'} color={'#05796B'} />}
                >
                  Add New
                </Button>
              </Col>
            )}
          </Row>
        </Col>
        <Col className={styles.notes}>
          <Row>
            {!!Object.keys(pinnedNotes)?.length && (
              <Col span={24} className={styles.pins_box}>
                <Row gutter={[0, 10]}>
                  <Col>
                    <Row align={'middle'}>
                      <PinIcon color={'#05796B'} />
                      <Typography.Text
                        className={'font-size-16 font-weight-600'}
                        style={{ color: '#05796B', marginLeft: 4 }}
                      >
                        Pins
                      </Typography.Text>
                    </Row>
                  </Col>
                  <Col span={24}>
                    {Object.keys(pinnedNotes).map((key) => (
                      <NoteGroup
                        key={key}
                        groupName={key}
                        entity={entity}
                        notes={pinnedNotes[key]}
                        hasAccessToOtherNoteWrite={hasAccessToOtherNoteWrite}
                        hasAccessToOtherNoteDelete={hasAccessToOtherNoteDelete}
                        hasAccessToWrite={hasAccessToWrite}
                        isFinalStatus={isFinalStatus}
                      />
                    ))}
                  </Col>
                </Row>
              </Col>
            )}
            {!!Object.keys(notes)?.length && (
              <Col span={24} className={styles.notes_box}>
                <Row>
                  {Object.keys(notes).map((key) => (
                    <NoteGroup
                      key={key}
                      groupName={key}
                      entity={entity}
                      notes={notes[key]}
                      lastNoteRef={lastNoteRef}
                      firstNoteRef={firstNoteRef}
                      hasAccessToWrite={hasAccessToWrite}
                      hasAccessToOtherNoteWrite={hasAccessToOtherNoteWrite}
                      hasAccessToOtherNoteDelete={hasAccessToOtherNoteDelete}
                      isFinalStatus={isFinalStatus}
                    />
                  ))}
                </Row>
              </Col>
            )}
          </Row>
          {!Object.keys(notes)?.length && !Object.keys(pinnedNotes)?.length && !isLoading && (
            <Row gutter={[0, 8]} justify={'center'} className={'margin-t-20'}>
              <TableEmptyIcon />
              <Col span={24}>
                <Title level={4} className={'font-weight-800 reset-title margin-t-4 center'}>
                  No Communication
                </Title>
              </Col>
              <Col flex={'365px'} className={'center'}>
                <Text type={'secondary'}>
                  No communications have been added yet. Please click “Add New” to create one.
                </Text>
              </Col>
            </Row>
          )}
        </Col>
      </Row>
      {isFetchingNextPage && (
        <Row justify={'center'} className={'margin-b-30'}>
          <Col span={24}>
            <Spin
              spinning={isFetchingNextPage}
              tip={'Loading Notes...'}
              indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            >
              <div />
            </Spin>
          </Col>
        </Row>
      )}
      <NoteAddUpdateModal modalState={addModalState} onModalStateChange={onAddModalStateChange} entity={entity} />
    </Spin>
  );
});
